import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getRegistrationForRestore } from "../lib/api";

const BRAND = { navy: "#161b3d", maroon: "#8b1e22" };

const ERROR_MESSAGES: Record<string, string> = {
  cancel: "Favor de volver a intentar.",
  missing_order: "No se recibió el número de orden.",
  registration_not_found: "Inscripción no encontrada.",
  invalid_school: "Inscripción no válida.",
  amount_mismatch: "El monto no coincide con la inscripción.",
  transaction_failed: "No se pudo reservar el cupo. Intente de nuevo o contacte al colegio.",
  invalid_signature: "Verificación de pago inválida.",
  duplicate_student_code: "Ya existe un estudiante con este código. Use otro código o contacte al colegio.",
};

/**
 * Azul return: user is redirected here by azulReturnHandler (approved / declined / error).
 * Reads query params (registrationId, error) and shows the appropriate message.
 */
export function AzulPaymentResult() {
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get("registrationId") ?? "";
  const studentIdFromUrl = searchParams.get("studentId") ?? "";
  const errorCode = searchParams.get("error") ?? "";
  const path = window.location.pathname;
  const [studentId, setStudentId] = useState<string | null>(studentIdFromUrl || null);
  const [studentCode, setStudentCode] = useState<string | null>(null);

  const isApproved = path.includes("/approved");
  const displayStudentId = studentIdFromUrl || studentId;

  useEffect(() => {
    if (!registrationId) return;
    if (studentIdFromUrl) setStudentId(studentIdFromUrl);
    getRegistrationForRestore(registrationId)
      .then((res) => {
        if (res.studentId) setStudentId((prev) => prev || (res.studentId ?? null));
        if (res.studentCode) setStudentCode(res.studentCode ?? null);
      })
      .catch((err) => console.warn("[audit]", err));
  }, [registrationId, studentIdFromUrl]);

  const pagosUrl = studentCode ? `/pagos/${encodeURIComponent(studentCode)}` : "/";
  const isDeclined = path.includes("/declined");
  const isCancel = path.includes("/cancel");
  const isError = path.includes("/error");

  if (isApproved) {
    return (
      <div className="min-h-screen bg-[#fbfbfd] flex flex-col items-center justify-center p-6 py-14">
        <div className="w-full max-w-3xl">
          <div className="bg-white rounded-2xl overflow-hidden text-center shadow-[0_4px_6px_rgba(0,0,0,0.02),0_24px_48px_rgba(0,0,0,0.06),0_0_0_1px_rgba(0,0,0,0.02)]">
            <div className="p-8 md:p-12 space-y-6">
              <div
                className="w-[72px] h-[72px] rounded-full flex items-center justify-center mx-auto bg-[rgba(52,199,89,0.1)]"
                aria-hidden
              >
                <svg className="w-9 h-9 text-[#34c759]" fill="none" stroke="currentColor" strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-[26px] md:text-[28px] font-semibold text-[#1d1d1f] tracking-tight leading-tight">
                Pago completado
              </h1>
              <p className="text-base text-[#1d1d1f] leading-[1.55] max-w-xl mx-auto">
                El pago de la inscripción se ha completado correctamente.
              </p>
              <p className="text-base text-[#86868b] leading-[1.5] max-w-xl mx-auto">
                Hemos enviado un correo electrónico de confirmación con su recibo a la dirección registrada.
              </p>
              {(displayStudentId || studentCode) && (
                <div className="inline-flex flex-col items-center gap-2 rounded-2xl py-4 px-6 bg-black/[0.03]">
                  <>
                    <span className="text-sm font-medium text-[#86868b] uppercase tracking-wider">
                      Tu ID de estudiante (matrícula)
                    </span>
                    <span className="text-base font-semibold text-[#1d1d1f] font-mono tracking-tight">
                      {displayStudentId || studentCode}
                    </span>
                    <span className="text-sm text-[#86868b]">
                      Guárdalo para consultar clases y pagos.
                    </span>
                  </>
                </div>
              )}
              <div className="pt-4">
                <Link
                  to={pagosUrl}
                  className="inline-block py-3.5 px-8 rounded-full font-semibold text-base text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                  style={{ backgroundColor: BRAND.navy }}
                >
                  {studentCode ? "Ver Mis clases" : "Volver al inicio"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isDeclined || isCancel) {
    const backUrl = registrationId ? `/?restore=${encodeURIComponent(registrationId)}` : pagosUrl;
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center p-8">
        <div className="max-w-2xl w-full bg-white rounded-2xl border border-gray-100 shadow-[0_8px_40px_rgba(0,0,0,0.06)] p-8 text-center space-y-6">
          <p className="text-lg font-semibold text-[#8b1e22]">
            {isCancel ? "Pago cancelado" : "Pago pendiente"}
          </p>
          {!isCancel && (
            <p className="text-base text-[#86868b]">
              El pago fue rechazado. Intente de nuevo o contacte al colegio.
            </p>
          )}
          <div className="space-y-4">
            {registrationId && (
              <Link
                to={studentCode ? `/pay/by-id/${encodeURIComponent(registrationId)}?returnCode=${encodeURIComponent(studentCode)}` : `/pay/by-id/${encodeURIComponent(registrationId)}`}
                className="block w-full py-3 rounded-xl font-semibold text-base text-white transition-all hover:opacity-95"
                style={{ backgroundColor: BRAND.navy }}
              >
                Intentar pagar de nuevo
              </Link>
            )}
            <Link
              to={backUrl}
              className="block w-full py-3 text-base font-semibold transition-colors hover:opacity-80"
              style={{ color: BRAND.navy }}
            >
              Volver atrás
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    const isCancel = errorCode === "cancel";
    const title = isCancel ? "Pago cancelado" : "Error en el pago";
    const message = errorCode ? (ERROR_MESSAGES[errorCode] ?? `Error: ${errorCode}`) : "Ocurrió un error.";
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center p-8">
        <div className="max-w-2xl w-full bg-white rounded-2xl border border-gray-100 shadow-[0_8px_40px_rgba(0,0,0,0.06)] p-8 text-center space-y-6">
          <p className="text-lg font-semibold text-red-600">{title}</p>
          <p className="text-base text-[#86868b]">{message}</p>
          <div className="space-y-4">
            {registrationId && !isCancel && (
              <Link
                to={studentCode ? `/pay/by-id/${encodeURIComponent(registrationId)}?returnCode=${encodeURIComponent(studentCode)}` : `/pay/by-id/${encodeURIComponent(registrationId)}`}
                className="block w-full py-3 rounded-xl font-semibold text-base text-white transition-all hover:opacity-95"
                style={{ backgroundColor: BRAND.navy }}
              >
                Intentar de nuevo
              </Link>
            )}
            <Link
              to={registrationId && isCancel ? `/?restore=${encodeURIComponent(registrationId)}` : pagosUrl}
              className="block w-full py-3.5 rounded-xl font-semibold text-base text-center border-2 transition-all hover:opacity-90"
              style={{ borderColor: BRAND.navy, color: BRAND.navy }}
            >
              Volver atrás
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center p-8">
      <Link to={pagosUrl} className="text-base font-medium" style={{ color: BRAND.navy }}>
        Volver al inicio
      </Link>
    </div>
  );
}
